/**
 * useFollowUpPage
 * Orquestra a página: aba ativa, templates, sheet e histórico (KPI + tabela).
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { notify } from '../../../../utils/toast';
import { useFollowUpTemplates, type FollowUpTemplateDraft } from './useFollowUpTemplates';
import { useFollowUpHistory, type FollowUpHistoryStatus } from './useFollowUpHistory';
import { useFollowUpSettings } from './useFollowUpSettings';
import { useFollowUpDestinationPicker } from './useFollowUpDestinationPicker';
import type { FollowUpDelayUnit, FollowUpSheetPayload, FollowUpTabId } from '../types';

const noopAddStage = (_stageId: string) => undefined;

export function useFollowUpPage() {
    const [activeTab, setActiveTab] = useState<FollowUpTabId>('followups');
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<FollowUpTemplateDraft | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [inlineMessage, setInlineMessage] = useState('');
    const [inlineMediaUrl, setInlineMediaUrl] = useState<string | null>(null);

    const {
        templates,
        loading,
        saving,
        add,
        duplicate,
        update,
        remove,
    } = useFollowUpTemplates();
    const settings = useFollowUpSettings();
    const { selectedRows: destinationRows } = useFollowUpDestinationPicker(settings.stageIds, noopAddStage);

    const history = useFollowUpHistory({ includeRows: activeTab === 'history' });
    const setHistoryStatus = history.setStatus;

    const openHistory = useCallback((status: FollowUpHistoryStatus) => {
        setHistoryStatus(status);
        setActiveTab('history');
    }, [setHistoryStatus]);

    const inlineRef = useRef({ id: null as string | null, message: '', media: null as string | null });
    inlineRef.current = { id: expandedId, message: inlineMessage, media: inlineMediaUrl };

    const flushInline = useCallback(() => {
        const { id, message, media } = inlineRef.current;
        if (!id) return;
        const current = templates.find((template) => template.id === id);
        if (!current) return;
        if (message === current.message && media === (current.mediaUrl ?? null)) return;
        void update(id, { message, mediaUrl: media }, { silent: true });
    }, [templates, update]);

    const openCreateSheet = useCallback(() => {
        flushInline();
        setExpandedId(null);
        setEditingTemplate(null);
        setIsSheetOpen(true);
    }, [flushInline]);

    const openEditSheet = useCallback((template: FollowUpTemplateDraft) => {
        flushInline();
        setExpandedId(null);
        setEditingTemplate(template);
        setIsSheetOpen(true);
    }, [flushInline]);

    const reloadSettings = settings.reload;
    const closeSheet = useCallback(() => {
        setIsSheetOpen(false);
        setEditingTemplate(null);
        reloadSettings();
    }, [reloadSettings]);

    const handleSheetSave = useCallback(async (
        payload: FollowUpSheetPayload,
        options?: { silent?: boolean },
    ) => {
        if (editingTemplate) {
            await update(editingTemplate.id, payload, { silent: options?.silent ?? false });
            return;
        }
        await add(payload);
    }, [add, editingTemplate, update]);

    const toggleActive = useCallback(async (id: string, isActive: boolean) => {
        await update(id, { isActive }, { silent: true });
        notify.success(isActive ? 'Follow-up ativado.' : 'Follow-up desativado.');
    }, [update]);

    const updateDelay = useCallback(async (
        id: string,
        delayValue: number,
        delayUnit: FollowUpDelayUnit,
    ) => {
        await update(id, { delayValue, delayUnit }, { silent: true });
    }, [update]);

    const expandedTemplate = templates.find((template) => template.id === expandedId) ?? null;
    const expandedMessage = expandedTemplate?.message;
    const expandedMediaUrl = expandedTemplate?.mediaUrl ?? null;
    const skipInlineHydrate = useRef(false);

    const toggleExpand = useCallback((id: string) => {
        flushInline();
        setExpandedId((current) => (current === id ? null : id));
    }, [flushInline]);

    const removeTemplate = useCallback(async (id: string) => {
        if (expandedId === id) {
            setExpandedId(null);
            inlineRef.current = { id: null, message: '', media: null };
        }
        await remove(id);
    }, [expandedId, remove]);

    useEffect(() => {
        if (!expandedId || expandedMessage === undefined) return;
        skipInlineHydrate.current = true;
        setInlineMessage(expandedMessage);
        setInlineMediaUrl(expandedMediaUrl);
        // Só ao trocar o card expandido — senão o save silencioso re-hidrata e entra em loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedId]);

    useEffect(() => {
        if (!expandedId || expandedMessage === undefined) return;
        if (skipInlineHydrate.current) {
            skipInlineHydrate.current = false;
            return;
        }
        if (inlineMessage === expandedMessage && inlineMediaUrl === expandedMediaUrl) return;
        const timer = window.setTimeout(() => {
            void update(expandedId, { message: inlineMessage, mediaUrl: inlineMediaUrl }, { silent: true });
        }, 700);
        return () => window.clearTimeout(timer);
    }, [inlineMessage, inlineMediaUrl, expandedId, expandedMessage, expandedMediaUrl, update]);

    return {
        activeTab,
        setActiveTab,
        templates,
        loading,
        saving,
        remove: removeTemplate,
        duplicate,
        toggleActive,
        updateDelay,
        expandedId,
        toggleExpand,
        inlineMessage,
        setInlineMessage,
        inlineMediaUrl,
        setInlineMediaUrl,
        isSheetOpen,
        editingTemplate,
        openCreateSheet,
        openEditSheet,
        closeSheet,
        handleSheetSave,
        destinationRows,
        history,
        openHistory,
    };
}
